import React, { useRef, useEffect, useCallback } from 'react';
import { LoadingOverlay, Title } from '@mantine/core';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import VerticalButtonBar from '../VerticalButtonBar';
import NavigHeaderBar from '../NavigHeaderBar/NavigHeaderBar';
import NavigTreeResizePaneContainer from '../NavigTreeResizePaneContainer/NavigTreeResizePaneContainer.js';
import { NODE_NAVIG_WIDTH, NUM_LEVEL } from '../../../constants/layout.js';
import NavigPanelContent from './NavigPanelContent';
import {
  ColumnsNodeContainer,
  DisableScrollMobileView,
  DivNavigPanel,
  NodeContainerMobile,
  SVGPanel,
  WrapItemEmpty,
} from './NavigTreeCSS';
import SelectedList from './SelectedList';
import NavigTreeColumns from './NavigTreeColums.js';
import { buildCardEnvelop, buildNodes } from './nodeBuilderUtils.js';
import { initializeStatsMap } from './statsUtils';
import ObjectActionCreators from '../../../actions.js';
import {
  setTimeLineCentralDate,
  setViewFilters,
} from '../../../reducers/objectReducers/action.js';
import {
  selectDisplayModel,
  selectFusedLevelWidth,
} from '../../../reducers/layoutReducer/displayModel.js';
import { splitPattern } from '../../Utils/TextUtils';
import HeaderCardsContainer from '../NavigHeader/headerCardsContainer.js';
import { useScales } from './useScale.js';
import { getObjectDefTrg } from '../../Utils/Utils.js';
import NavigBand from '../NavigBand/NavigBand.js';
import { getBandsPosition } from '../../Utils/LayoutUtils.js';

import { FilterConsoleBound } from '../../UI/FilterConsole/FilterConsole.js';
import config from '../../../config.json';

const ENVELOP_OFFSET_X = 40;

const NavigTree = ({
  graphHeight,
  isMobile,
  isResizePane,
  handlers,
  treeNodes,
  svgRef,
  draggedItem,
  scalesConfig,
  displayMode,
  handleDrop,
  zoom,
  isLoadingTree,
  autoVertPosMS,
  onUpdateNodeHeight,
  perspectiveId,
  panels,
  handleScroll,
}) => {
  const { t } = useTranslation();
  const scrollRefY = useRef(null);
  const dataMap = initializeStatsMap();
  const dispatch = useDispatch();
  const arrayStatBy = useSelector((state) => state.objects.arrayStatBy);
  const { fusedDataColumns } = useSelector(selectDisplayModel);
  const fusedLevelWidth = useSelector(selectFusedLevelWidth);
  const pattern = useSelector((state) => state.objects.pattern);

  const objectsDefList = useSelector((state) => state.objects.objectsDefList);
  const setTimeLineChangeCentral = useCallback((level, value) => {
    dispatch(setTimeLineCentralDate(level, value));
  });

  const viewFiltersConfig = useSelector(
    (state) => state.objects.viewFiltersConfig
  );
  const setViewFilt = useCallback((viewFilters, viewFiltersRestrictions) => {
    dispatch(setViewFilters(viewFilters, viewFiltersRestrictions));
  });
  const getObjectType = (index) => {
    return splitPattern(pattern)[index];
  };
  // Scroll handling
  useEffect(() => {
    const scrollContainer = document.getElementById('snap_scroll_container');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handlers.handleScroll);
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handlers.handleScroll);
      }
    };
  }, [handlers]);

  useEffect(() => {
    let timeoutId;
    const handleScrollY = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        if (scrollRefY.current) {
          handleScroll(scrollRefY.current.scrollTop);
        }
      }, +autoVertPosMS);
    };

    if (scrollRefY.current) {
      scrollRefY.current.addEventListener('scroll', handleScrollY);
    }
    return () => {
      if (scrollRefY.current) {
        scrollRefY.current.removeEventListener('scroll', handleScrollY);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [handleScroll, autoVertPosMS]);

  // Scroll to top when perspective changes
  useEffect(() => {
    if (scrollRefY.current) {
      scrollRefY.current.scrollTop = 0;
    }
  }, [perspectiveId]);

  const update = (nodes) => {
    let res = [];
    let nodesRes = buildNodes({
      draggedItem,
      nodes,
      dataMap,
      myNodeColorScale: scalesConfig.colorScales,
      displayMode,
      fusedDataColumns,
      isMobile,
      bands_config: panels,
      arrayClassifBy: scalesConfig.arrayClassifBy,
      arrayStatBy,
      eventHandlers: handlers,
      updateNodeHeight: onUpdateNodeHeight,
      handleDrop,
      setTimeLineChangeCentral,
      fusedLevelWidth,
    });
    if (nodes) {
      let envelopRes = buildCardEnvelop({ nodes, displayMode })?.map(
        (env, ind) => {
          const objectType = getObjectType(env.depth - 1);
          const fieldName = scalesConfig.arrayClassifBy[env.depth - 1];
          const defField = getObjectDefTrg(
            objectsDefList,
            objectType,
            fieldName
          );
          const band = getBandsPosition(env.depth - 1, panels);
          const availableSpace = band.width - NODE_NAVIG_WIDTH;
          return (
            <div
              style={{
                width: band.width - 10,
                top: env.ymin - 40,
                height: env.ymax - env.ymin + 80,
                border: '1px dashed var(--ds-border-base)',
                position: 'absolute',
                borderRadius: '5px',
                backgroundColor: '#D0DDEB33',
                left: -ENVELOP_OFFSET_X,
                paddingLeft: ENVELOP_OFFSET_X,
              }}
            >
              <NavigBand
                offsetX={ENVELOP_OFFSET_X}
                key={`bands_${env.id}`}
                idNodeParent={env.id}
                arrayClassif={scalesConfig.bandValues[env.depth - 1]}
                fieldType={defField?.type}
                level={env.depth - 1}
                colorBandFct={scalesConfig.colorScales[env.depth - 1]}
                objectType={objectType}
                onDrop={handleDrop}
                scalerXBand={scalesConfig.bandScales[env.depth - 1]}
                statsMap={dataMap}
                type={fieldName}
                options={defField?.options}
                availableSpace={availableSpace}
                bandValues={scalesConfig.bandValues}
                bandScales={scalesConfig.bandScales}
                // scaleUpdateCounter={scaleUpdateCounter}
              />
              <HeaderCardsContainer
                key={`hcc_${env.id}`}
                level={env.depth - 1}
                // x={left}
                y={0}
                width={availableSpace}
              />
            </div>
          );
        }
      );
      res.push(envelopRes);
    }
    res.push(nodesRes);

    return res;
  };

  const width =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;
  const nodesByLevel = Array.from({ length: NUM_LEVEL }, (_, index) =>
    treeNodes.filter((a) => a.depth - 1 === index)
  );

  if (isMobile) {
    const height =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight;

    return (
      <div
        id="test"
        style={{ overflowY: 'scroll', touchAction: 'pan-y', height }}
      >
        <LoadingOverlay
          visible={isLoadingTree[0]} // assuming first level loading state
          overlayBlur={2}
          transitionDuration={500}
          overlayOpacity={0.3}
          overlayColor="#c5c5c5"
          zIndex={6000}
        />
        <NodeContainerMobile id="snap_scroll_container">
          {nodesByLevel.map((nodes, index) => {
            const isNextColumnEmpty = isEmpty(nodes);
            return (
              <div
                key={index}
                style={{
                  scrollSnapAlign: 'start',
                  zIndex: 10,
                  touchAction: 'pan-y pan-x',
                  position: 'relative',
                }}
              >
                <DisableScrollMobileView
                  id="disable_scroll_mobile"
                  $isNextColumnNotHaveContent={isNextColumnEmpty}
                />
                <ColumnsNodeContainer>
                  {isNextColumnEmpty && (
                    <WrapItemEmpty>
                      <Title
                        style={{
                          color: 'var(--ds-text-secondary)',
                          textAlign: 'center',
                        }}
                        order={6}
                      >
                        {t('display.no_data')}
                      </Title>
                    </WrapItemEmpty>
                  )}
                  {update(nodes, 'default', index)}
                </ColumnsNodeContainer>
              </div>
            );
          })}
        </NodeContainerMobile>
      </div>
    );
  }

  return (
    <>
      <SelectedList />
      <VerticalButtonBar />
      <DivNavigPanel
        onScroll={handlers.handleOnScroll}
        $zoom={zoom}
        id="divNavigPanel_handle_scroll_y"
        ref={scrollRefY}
        $isResizePane={isResizePane}
      >
        {config.filterConsole.isFilterConsoleEnabled && (
          <FilterConsoleBound
            // ex: fieldsByType={[
            //   { objTypeId: 'framework1756587893836', fieldNames: ['name'] },
            //   {
            //     objTypeId: 'domains1755861179079',
            //     fieldNames: ['domainpriority'],
            //   },

            //   {
            //     objTypeId: 'actions1755861182210',
            //     fieldNames: ['status', 'deadline', 'assignee'],
            //   },
            // ]}

            fieldsByType={viewFiltersConfig}
            onChange={({ filters, filterRestriction }) => {
              setViewFilt(filters, filterRestriction);
              // ex: filters = ['framework6776','action9798']
              //     filterRestriction = ["name =~ '.*iso.*'", "status IN ['done'] AND assignee = 'employee98798'"]
              // -> déclenche ta (re)query de vue ici
            }}
            onOpenObjectPicker={''}
          />
        )}
        <NavigHeaderBar
          scalesConfig={scalesConfig}
          handleDrop={handleDrop}
          dataMap={dataMap}
          nodes={treeNodes}
        />
        <NavigPanelContent height={Math.max(graphHeight, window.innerHeight)}>
          <NavigTreeResizePaneContainer>
            <NavigTreeColumns
              scalesConfig={scalesConfig}
              nodes={treeNodes}
              update={update}
              handleDrop={handleDrop}
            />
          </NavigTreeResizePaneContainer>
          <SVGPanel id="svgPanel" width="500%" height={graphHeight}>
            <svg ref={svgRef} />
          </SVGPanel>
        </NavigPanelContent>
      </DivNavigPanel>
    </>
  );
};

export default React.memo(NavigTree);
